<template>
  <component
    :is="cardTag"
    class="ca-testimonial-card"
    :class="`ca-testimonial-card--${normalizedCard.type}`"
    :href="cardHref"
    :target="cardTarget"
    :rel="cardRel"
  >
    <img
      v-if="hasMedia"
      class="ca-testimonial-card__media"
      :src="mediaSource"
      :alt="normalizedCard.mediaUrl && !mediaFailed ? normalizedCard.mediaAlt : ''"
      loading="lazy"
      @error="handleMediaError"
    />
    <span
      v-if="hasMedia"
      class="ca-testimonial-card__overlay"
      aria-hidden="true"
    ></span>

    <div v-if="isText" class="ca-testimonial-card__content">
      <div class="ca-testimonial-card__profile">
        <div class="ca-testimonial-card__profile-header">
          <img
            class="ca-testimonial-card__avatar"
            :src="avatarSource"
            :alt="normalizedCard.avatarUrl ? normalizedCard.avatarAlt : ''"
            @error="handleAvatarError"
          />

          <span class="ca-testimonial-card__quote" aria-hidden="true">
            <img
              v-if="normalizedCard.quoteIconUrl && !quoteIconFailed"
              :src="normalizedCard.quoteIconUrl"
              alt=""
              @error="quoteIconFailed = true"
            />
            <TestimonialQuoteIcon v-else />
          </span>
        </div>

        <div class="ca-testimonial-card__identity">
          <component
            :is="normalizeHeadingTag(normalizedCard.nameHeadingType, 'p')"
            class="ca-testimonial-card__name caption-action"
          >
            {{ normalizedCard.name }}
          </component>
          <component
            :is="normalizeHeadingTag(normalizedCard.subtitleHeadingType, 'p')"
            class="ca-testimonial-card__subtitle caption-large"
          >
            {{ normalizedCard.subtitle }}
          </component>
        </div>
      </div>

      <component
        :is="normalizeHeadingTag(normalizedCard.messageHeadingType, 'p')"
        class="ca-testimonial-card__message paragraph-small"
      >
        {{ normalizedCard.message }}
      </component>
    </div>

    <template v-else>
      <IconButton
        v-if="hasVideoLink"
        class="ca-testimonial-card__play"
        type="inverted"
        variant="primary"
        size="xl"
        shadow
        :aria-label="normalizedCard.videoLabel"
        @clicked="openVideo"
      >
        <template #trailingIcon>
          <TestimonialPlayIcon aria-hidden="true" />
        </template>
      </IconButton>

      <component
        v-if="normalizedCard.description"
        :is="normalizeHeadingTag(normalizedCard.descriptionHeadingType, 'p')"
        class="ca-testimonial-card__media-description heading-regular"
      >
        {{ normalizedCard.description }}
      </component>
    </template>
  </component>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { IconButton } from "ca-online-design-system";
import TestimonialPlayIcon from "@assets/play.svg";
import TestimonialQuoteIcon from "@assets/testimonial-quote.svg";
import testimonialAvatarPlaceholder from "@assets/testimonial-avatar-placeholder.png";
import testimonialMediaFallback from "@assets/testimonial-media-fallback.png";
import testimonialMediaPlaceholder from "@assets/testimonial-media-placeholder.png";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "CaTestimonialCard",
});

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["video-play"]);
const mediaFailed = ref(false);
const avatarFailed = ref(false);
const quoteIconFailed = ref(false);

const normalizedCard = computed(() => {
  const card = props.card ?? {};
  const type = ["text", "image", "video"].includes(
    String(card.type).toLowerCase(),
  )
    ? String(card.type).toLowerCase()
    : "text";
  const name = readText(card.name);
  const link = card.videoLink ?? card.link;
  const url = readUrl(card.url ?? card.link);
  const videoUrl = readUrl(
    card.videoUrl ?? card.videoLink ?? (type !== "text" ? card.link ?? card.url : ""),
  );

  return {
    type,
    name,
    nameHeadingType: card.nameHeadingType,
    subtitle: readText(card.subtitle ?? card.position ?? card.role),
    subtitleHeadingType: card.subtitleHeadingType,
    message: readText(card.message ?? card.bodyMessage ?? card.text),
    messageHeadingType: card.messageHeadingType,
    description: readText(card.description ?? card.mediaDescription),
    descriptionHeadingType: card.descriptionHeadingType,
    avatarUrl: readText(card.avatarUrl ?? card.avatarImageUrl),
    avatarAlt: readText(card.avatarAlt) || name,
    avatarPlaceholderUrl: readText(card.avatarPlaceholderUrl),
    mediaUrl: readText(card.mediaUrl ?? card.imageUrl ?? card.thumbnailUrl),
    mediaAlt: readText(card.mediaAlt ?? card.alt),
    mediaPlaceholderUrl: readText(card.mediaPlaceholderUrl),
    mediaFallbackUrl: readText(card.mediaFallbackUrl),
    quoteIconUrl: readText(card.quoteIconUrl ?? card.quoteSvgUrl),
    url,
    target: readText(card.target) || "_self",
    videoUrl,
    videoTarget:
      readText(card.videoTarget ?? link?.target ?? card.target) || "_blank",
    videoLabel:
      readText(card.videoLabel) ||
      `Reproduzir vídeo${name ? `: ${name}` : ""}`,
  };
});

const isText = computed(() => normalizedCard.value.type === "text");
const hasMedia = computed(() => !isText.value);
const hasVideoLink = computed(() => Boolean(normalizedCard.value.videoUrl));
const avatarSource = computed(() => {
  if (avatarFailed.value) {
    return normalizedCard.value.avatarPlaceholderUrl || testimonialAvatarPlaceholder;
  }

  return (
    normalizedCard.value.avatarUrl ||
    normalizedCard.value.avatarPlaceholderUrl ||
    testimonialAvatarPlaceholder
  );
});
const mediaSource = computed(() => {
  if (mediaFailed.value) {
    return normalizedCard.value.mediaFallbackUrl || testimonialMediaFallback;
  }

  return (
    normalizedCard.value.mediaUrl ||
    normalizedCard.value.mediaPlaceholderUrl ||
    testimonialMediaPlaceholder
  );
});
const cardTag = computed(() =>
  !hasVideoLink.value && normalizedCard.value.url ? "a" : "article",
);
const cardHref = computed(() =>
  cardTag.value === "a" ? normalizedCard.value.url : undefined,
);
const cardTarget = computed(() =>
  cardTag.value === "a" ? normalizedCard.value.target : undefined,
);
const cardRel = computed(() =>
  cardTarget.value === "_blank" ? "noopener noreferrer" : undefined,
);

watch(
  () => [
    normalizedCard.value.avatarUrl,
    normalizedCard.value.mediaUrl,
    normalizedCard.value.quoteIconUrl,
  ],
  () => {
    mediaFailed.value = false;
    avatarFailed.value = false;
    quoteIconFailed.value = false;
  },
);

function readText(value) {
  return String(value ?? "");
}

function readUrl(value) {
  if (value && typeof value === "object") {
    return readUrl(value.url ?? value.href ?? value.value);
  }

  return readText(value);
}

function handleAvatarError() {
  if (!avatarFailed.value) avatarFailed.value = true;
}

function handleMediaError() {
  if (!mediaFailed.value) mediaFailed.value = true;
}

function openVideo() {
  const { videoUrl, videoTarget } = normalizedCard.value;

  emit("video-play", props.card);
  if (!videoUrl) return;

  if (videoTarget === "_blank") {
    window.open(videoUrl, "_blank", "noopener,noreferrer");
    return;
  }

  window.location.assign(videoUrl);
}
</script>

<style scoped lang="scss">
.ca-testimonial-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--size-5);
  width: 100%;
  min-width: 0;
  padding: var(--size-5);
  overflow: hidden;
  border-radius: var(--size-5);
  color: var(--ca-color-text-body);
  background-color: var(--ca-color-neutrals-inverted);
  text-decoration: none;
}

.ca-testimonial-card--text {
  max-height: 480px;
  box-shadow: 0 2px 6px rgb(0 0 0 / 10%);
}

.ca-testimonial-card--image,
.ca-testimonial-card--video {
  height: 480px;
  min-height: 480px;
  justify-content: flex-end;
  color: var(--ca-color-text-inverted);
  background-color: var(--ca-color-neutrals-on-background);
}

.ca-testimonial-card--image {
  gap: var(--size-6);
}

.ca-testimonial-card__media,
.ca-testimonial-card__overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.ca-testimonial-card__media {
  display: block;
  object-fit: cover;
}

.ca-testimonial-card__overlay {
  background: linear-gradient(
    180deg,
    rgb(51 60 69 / 0%) 50%,
    var(--ca-color-neutrals-on-background-high) 95%
  );
}

.ca-testimonial-card__content,
.ca-testimonial-card__profile,
.ca-testimonial-card__identity {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
}

.ca-testimonial-card__content {
  position: relative;
  z-index: 1;
  gap: var(--size-5);
}

.ca-testimonial-card__profile {
  gap: var(--size-3);
}

.ca-testimonial-card__profile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.ca-testimonial-card__avatar {
  display: block;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border-radius: 50%;
  object-fit: cover;
}

.ca-testimonial-card__quote {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
}

.ca-testimonial-card__quote img,
.ca-testimonial-card__quote :deep(svg) {
  display: block;
  width: 24px;
  height: 19.448px;
}

.ca-testimonial-card__identity {
  gap: var(--size-2);
  min-height: 24px;
  padding-block: var(--size-3);
}

.ca-testimonial-card__name,
.ca-testimonial-card__subtitle,
.ca-testimonial-card__message {
  width: 100%;
  margin: 0;
  overflow-wrap: anywhere;
}

.ca-testimonial-card .ca-testimonial-card__name {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.192px;
  word-spacing: 0.256px;
  color: var(--ca-color-text-headers);
}

.ca-testimonial-card .ca-testimonial-card__subtitle {
  color: var(--ca-color-text-body-variant);
}

@media (max-width: 1023px) {
  .ca-testimonial-card .ca-testimonial-card__subtitle {
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: 0.168px;
    word-spacing: 0.224px;
  }
}

@media (min-width: 1024px) {
  .ca-testimonial-card .ca-testimonial-card__subtitle {
    font-size: 16px;
  }
}

.ca-testimonial-card .ca-testimonial-card__message {
  min-height: 24px;
  padding-block: var(--size-3);
  color: var(--ca-color-text-body);
}

.ca-testimonial-card .ca-testimonial-card__media-description {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 0;
  overflow-wrap: anywhere;
  color: var(--ca-color-text-inverted);
}

.ca-testimonial-card__play.base-button {
  position: absolute;
  top: var(--size-5);
  right: var(--size-5);
  z-index: 2;
  border-color: transparent;
}

.ca-testimonial-card__play.base-button:focus:not(:focus-visible) {
  outline: none;
}

.ca-testimonial-card__play img,
.ca-testimonial-card__play :deep(svg) {
  display: block;
  width: var(--size-5);
  height: var(--size-5);
}

.ca-testimonial-card:focus-visible {
  outline: 2px solid var(--ca-color-outline-focus-only);
  outline-offset: 3px;
}
</style>